import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Group,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconEdit,
  IconPlus,
  IconTag,
  IconTrash,
} from "@tabler/icons-react";
import { useResponsive } from "../../../context/ResponsiveContext";
import { useAppointmentTags } from "../../../hooks/useAppointmentTags";
import usePermissions from "../../../hooks/usePermissions";
import { AppointmentTag } from "../../../types/appointmentTags";
import { notifyError, notifySuccess } from "../../../utils/notify";
import AppointmentTagChip from "../../appointmentTags/AppointmentTagChip";
import DeleteConfirmationModal from "../../common/DeleteConfirmationModal";
import { StandardTableShell } from "../../common/StandardTable";
import EmptyStateCard from "../../common/surface/EmptyStateCard";
import {
  SettingsListSkeleton,
  SettingsPageHeader,
  SettingsPageLayout,
} from "../common";
import AppointmentTagFormModal from "./AppointmentTagFormModal";
import styles from "./AppointmentTagsSettings.module.css";

export const deleteTagMessage = (tag: AppointmentTag): string =>
  `Delete the "${tag.name}" tag? It will be removed from every appointment ` +
  "that has it. You can create a new tag with the same name later.";

interface TagActionsProps {
  tag: AppointmentTag;
  onEdit: (tag: AppointmentTag) => void;
  onDelete: (tag: AppointmentTag) => void;
}

/** Edit / Delete icon pair with ≥44px touch targets. */
function TagActions({ tag, onEdit, onDelete }: TagActionsProps) {
  return (
    <Group gap={4} wrap="nowrap">
      <ActionIcon
        variant="subtle"
        color="teal"
        size="lg"
        className={styles.touchTarget}
        aria-label={`Edit ${tag.name}`}
        onClick={() => onEdit(tag)}
      >
        <IconEdit size={18} />
      </ActionIcon>
      <ActionIcon
        variant="subtle"
        color="var(--color-danger)"
        size="lg"
        className={styles.touchTarget}
        aria-label={`Delete ${tag.name}`}
        onClick={() => onDelete(tag)}
      >
        <IconTrash size={18} />
      </ActionIcon>
    </Group>
  );
}

/**
 * Settings → Offerings → Appointment Tags: the location's tag catalog (name +
 * optional emoji) that staff apply to appointments. Every staff member can
 * see the list; add / edit / delete need `calendar.canManageAppointmentTags`
 * (fails closed until /access serves the key). Table on desktop/tablet,
 * stacked cards on phones.
 */
export default function AppointmentTagsSettings() {
  const { listTags, deleteTag } = useAppointmentTags();
  const { isMobile } = useResponsive();
  const perms = usePermissions();
  const canManage = perms.can("calendar", "canManageAppointmentTags");

  const [tags, setTags] = useState<AppointmentTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [formOpened, setFormOpened] = useState(false);
  const [editingTag, setEditingTag] = useState<AppointmentTag | null>(null);
  const [deletingTag, setDeletingTag] = useState<AppointmentTag | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Drop a response that a newer fetch (e.g. a location switch) superseded.
  const reqIdRef = useRef(0);
  const refresh = useCallback(async () => {
    const myReqId = ++reqIdRef.current;
    setLoading(true);
    setError(false);
    try {
      const next = await listTags();
      if (myReqId === reqIdRef.current) setTags(next);
    } catch (err) {
      console.error("Error loading appointment tags:", err);
      if (myReqId === reqIdRef.current) setError(true);
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  }, [listTags]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openAdd = () => {
    setEditingTag(null);
    setFormOpened(true);
  };

  const openEdit = (tag: AppointmentTag) => {
    setEditingTag(tag);
    setFormOpened(true);
  };

  const closeForm = () => {
    setFormOpened(false);
    setEditingTag(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingTag) return;
    const { id, name } = deletingTag;
    setIsDeleting(true);
    try {
      await deleteTag(id);
      notifySuccess(`${name} was deleted.`, { title: "Tag deleted" });
      setDeletingTag(null);
      refresh();
    } catch (err) {
      console.error("Error deleting appointment tag:", err);
      notifyError(
        (err as Error | null)?.message ||
          "Failed to delete tag. Please try again.",
        { title: "Delete failed" },
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const renderBody = () => {
    if (loading && tags.length === 0) {
      return (
        <SettingsListSkeleton
          rows={3}
          ariaLabel="Loading tags"
          data-testid="appointment-tags-loading"
        />
      );
    }

    if (error && tags.length === 0) {
      return (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Couldn't load tags"
          color="var(--color-danger)"
        >
          <Group justify="space-between" align="center">
            <Text size="sm">
              Something went wrong loading your tags. Please try again.
            </Text>
            <Button
              size="xs"
              variant="light"
              color="var(--color-danger)"
              onClick={() => refresh()}
            >
              Retry
            </Button>
          </Group>
        </Alert>
      );
    }

    if (tags.length === 0) {
      return (
        <EmptyStateCard
          icon={<IconTag size={20} stroke={1.9} />}
          title="No tags yet"
          message={
            canManage
              ? "Create tags your team can add to appointments, like VIP or Referral."
              : "Your managers haven't created any appointment tags yet."
          }
          actionLabel={canManage ? "Add Tag" : undefined}
          onAction={canManage ? openAdd : undefined}
        />
      );
    }

    if (isMobile) {
      return (
        <Stack gap="sm">
          {tags.map((tag) => (
            <Box
              key={tag.id}
              className={styles.card}
              data-testid="appointment-tag-card"
            >
              <Box className={styles.cardBody}>
                <AppointmentTagChip name={tag.name} emoji={tag.emoji} />
              </Box>
              {canManage && (
                <TagActions
                  tag={tag}
                  onEdit={openEdit}
                  onDelete={setDeletingTag}
                />
              )}
            </Box>
          ))}
        </Stack>
      );
    }

    return (
      <StandardTableShell minWidth={360} ariaLabel="Appointment tags">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Tag</Table.Th>
            {canManage && <Table.Th w={110}>Actions</Table.Th>}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {tags.map((tag) => (
            <Table.Tr key={tag.id}>
              <Table.Td>
                <AppointmentTagChip name={tag.name} emoji={tag.emoji} />
              </Table.Td>
              {canManage && (
                <Table.Td>
                  <TagActions
                    tag={tag}
                    onEdit={openEdit}
                    onDelete={setDeletingTag}
                  />
                </Table.Td>
              )}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </StandardTableShell>
    );
  };

  return (
    <SettingsPageLayout
      header={
        <SettingsPageHeader
          title="Appointment Tags"
          subtitle="Labels your team can add to appointments, like VIP or Referral."
          actions={
            canManage ? (
              <Button
                color="var(--brand-teal)"
                leftSection={<IconPlus size={16} />}
                onClick={openAdd}
              >
                Add Tag
              </Button>
            ) : undefined
          }
        />
      }
    >
      {!canManage && (
        <Text
          size="sm"
          c="dimmed"
          mb="sm"
          data-testid="appointment-tags-readonly"
        >
          You can view these tags. Ask an owner or manager to add or change
          them.
        </Text>
      )}
      <Box>{renderBody()}</Box>

      {canManage && (
        <AppointmentTagFormModal
          opened={formOpened}
          onClose={closeForm}
          tag={editingTag}
          onSaved={refresh}
        />
      )}
      {canManage && (
        <DeleteConfirmationModal
          isOpen={deletingTag !== null}
          onClose={() => setDeletingTag(null)}
          onConfirm={handleDeleteConfirm}
          title="Delete Tag"
          message={deletingTag ? deleteTagMessage(deletingTag) : ""}
          isLoading={isDeleting}
          confirmColor="var(--color-danger)"
        />
      )}
    </SettingsPageLayout>
  );
}
